/* Bhairav Tatva — certificate verification page (sections/bt-certificate-verify.liquid).
   Reads ?r=<report number>, asks the store's app proxy (same domain) and renders the
   result. Everything from the response is written with textContent, never as HTML. */
(function () {
  'use strict';

  var REPORT_RE = /^[A-Z0-9]{3,20}$/;

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  // Accept the bare number, a pasted verify link, or a number typed with spaces.
  function normalise(raw) {
    var value = String(raw || '').trim();
    var match = value.match(/[?&]r=([^&#\s]+)/i);
    if (match) {
      try { value = decodeURIComponent(match[1]); } catch (e) { value = match[1]; }
    }
    return value.replace(/[^a-z0-9]/gi, '').toUpperCase();
  }

  function formatDate(iso) {
    if (!iso) return '';
    var date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    // Same calendar day as the date printed on the card (the backend prints
    // in its own time zone, UTC), wherever the visitor is.
    try {
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
    } catch (e) {
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }

  function init(root) {
    var proxy = (root.getAttribute('data-proxy') || '/apps/certificate').replace(/\/+$/, '');
    var brand = root.getAttribute('data-brand') || '';
    var form = root.querySelector('[data-bt-verify-form]');
    var input = root.querySelector('[data-bt-verify-input]');
    var result = root.querySelector('[data-bt-verify-result]');
    var messages = {};
    var tpl = root.querySelector('template[data-bt-verify-messages]');
    if (tpl && tpl.content) {
      Array.prototype.forEach.call(tpl.content.querySelectorAll('[data-key]'), function (node) {
        messages[node.getAttribute('data-key')] = node.textContent;
      });
    }
    var current = null;

    function notice(tone, title, detail) {
      var box = el('div', 'bt-verify__notice bt-verify__notice--' + tone);
      box.setAttribute('role', tone === 'loading' ? 'status' : 'alert');
      box.appendChild(el('p', 'bt-verify__notice-title', title));
      if (detail) box.appendChild(el('p', 'bt-verify__notice-text', detail));
      return box;
    }

    function row(table, label, value, className) {
      if (value === undefined || value === null || String(value).trim() === '') return;
      var tr = el('tr');
      tr.appendChild(el('th', null, label));
      tr.appendChild(el('td', className || null, value));
      table.appendChild(tr);
    }

    function documentCard(data) {
      var revoked = data.status === 'revoked';
      var card = el('article', 'bt-verify__doc' + (revoked ? ' is-revoked' : ''));
      var inner = el('div', 'bt-verify__doc-inner');
      card.appendChild(inner);

      var head = el('header', 'bt-verify__doc-head');
      head.appendChild(el('span', 'bt-verify__doc-brand', brand));
      head.appendChild(el('span', 'bt-verify__doc-kind', messages.doc_title || 'Certificate of Authenticity'));
      inner.appendChild(head);

      var body = el('div', 'bt-verify__doc-body');
      var media = el('figure', 'bt-verify__doc-media');
      var frame = el('div', 'bt-verify__doc-photo');
      // Only this store's own proxy photo path (never another host).
      if (typeof data.photo_url === 'string' && data.photo_url.indexOf(proxy + '/photo/') === 0 &&
          /^[A-Za-z0-9_\/-]+$/.test(data.photo_url)) {
        var img = el('img');
        img.src = data.photo_url;
        img.alt = data.item_name || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.addEventListener('error', function () { frame.classList.add('is-empty'); img.remove(); });
        frame.appendChild(img);
      } else {
        frame.classList.add('is-empty');
      }
      media.appendChild(frame);
      if (messages.photo_caption) media.appendChild(el('figcaption', null, messages.photo_caption));
      body.appendChild(media);

      var details = el('div', 'bt-verify__doc-details');
      details.appendChild(el('h2', 'bt-verify__doc-name', data.item_name));
      if (data.variety_note) details.appendChild(el('p', 'bt-verify__doc-variety', data.variety_note));
      var table = el('table', 'bt-verify__props');
      var tbody = el('tbody');
      table.appendChild(tbody);
      row(tbody, 'Report No.', data.report_no, 'bt-verify__mono');
      row(tbody, 'Date', formatDate(data.issued_on));
      (data.properties || []).forEach(function (prop) {
        if (prop) row(tbody, prop.label, prop.value);
      });
      if (revoked) row(tbody, 'Revoked on', formatDate(data.revoked_on), 'bt-verify__revoked-on');
      details.appendChild(table);
      body.appendChild(details);
      inner.appendChild(body);

      if (messages.doc_footer) inner.appendChild(el('footer', 'bt-verify__doc-foot', messages.doc_footer));
      return card;
    }

    function show(nodes) {
      result.textContent = '';
      nodes.forEach(function (node) { result.appendChild(node); });
    }

    function setQuery(reportNo) {
      try {
        var url = new URL(window.location.href);
        if (reportNo) url.searchParams.set('r', reportNo);
        else url.searchParams.delete('r');
        window.history.replaceState(null, '', url.toString());
      } catch (e) { /* older browsers: leave the URL as it is */ }
    }

    function lookup(raw) {
      var reportNo = normalise(raw);
      input.value = reportNo;
      if (!REPORT_RE.test(reportNo)) {
        show([notice('missing', messages.missing_title, messages.invalid_detail)]);
        return;
      }
      setQuery(reportNo);
      var token = {};
      current = token;
      show([notice('loading', messages.loading)]);

      fetch(proxy + '/verify/' + encodeURIComponent(reportNo), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
        .then(function (response) {
          return response.json().catch(function () { return null; }).then(function (payload) {
            return { status: response.status, payload: payload };
          });
        })
        .then(function (res) {
          if (current !== token) return;
          var payload = res.payload || {};
          if (res.status === 200 && payload.found && payload.data) {
            var revoked = payload.data.status === 'revoked';
            show([
              notice(revoked ? 'revoked' : 'valid', revoked ? messages.revoked_title : messages.valid_title, payload.detail),
              documentCard(payload.data)
            ]);
          } else if (res.status === 404) {
            show([notice('missing', messages.missing_title, messages.missing_detail)]);
          } else if (res.status === 429) {
            show([notice('busy', messages.busy_title, messages.busy_detail)]);
          } else {
            show([notice('error', messages.error_title, messages.error_detail)]);
          }
        })
        .catch(function () {
          if (current !== token) return;
          show([notice('error', messages.error_title, messages.error_detail)]);
        });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      lookup(input.value);
    });

    var initial = '';
    try { initial = new URL(window.location.href).searchParams.get('r') || ''; } catch (e) { initial = ''; }
    if (initial) lookup(initial);
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-bt-verify]'), function (root) {
      if (root.__btVerify) return;
      root.__btVerify = true;
      init(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', boot);
})();
